import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { BiErrorCircle } from "react-icons/bi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import Register from "./Register";
import { API_URL, PROD_LOGIN_LINK, STAGING_LOGIN_LINK } from "../constant";
import { IS_STAGING } from "../constant/api";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { logo } from "../assets/images";
import { FaMagic } from "react-icons/fa";
import { IoMailUnreadOutline } from "react-icons/io5";
import { CgScan } from "react-icons/cg";
import LoginWithEmail from "./LoginWithEmail";
import LoginWithOtp from "./LoginWithOtp";
import OtpScreen from "./OtpScreen";
import CodeScreen from "./CodeScreen";

const Login: FC = () => {
  const { authReady, isAuthenticated } = useAuth();
  const location = useLocation();
  const { state } = location;
  const { from = "/dashboard" } = state || {};
  const [selectedMethod, setSelectedMethod] = useState<
    "" | "magiclink" | "auth-app" | "email"
  >("");
  const [isBigScreen, setIsBigScreen] = useState(window.innerWidth >= 600); // adjust breakpoint

  useEffect(() => {
    const handleResize = () => {
      const big = window.innerWidth >= 600;
      setIsBigScreen(big);

      // if switching to big screen and no method is selected, default to magiclink
      if (big && selectedMethod === "") {
        setSelectedMethod("magiclink");
      }
    };

    window.addEventListener("resize", handleResize);

    // run once on mount
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [selectedMethod]);
  const navigate = useNavigate();

  const [otpScreen, setOtpScreen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [codeMail, setCodeMail] = useState<string>("");
  const [codeOtp, setCodeOtp] = useState<string>("");
  const [codeScreen, setCodeScreen] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [otpPayload, setOtpPayload] = useState<{
    email: string;
    password: string;
    deviceInfo: { name: string; uniqueId: string; os?: string };
    method: string;
  } | null>(null);

  const resolveMagicLinkBase = () => {
    const host = window.location.hostname.toLowerCase();
    const origin = window.location.origin;
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "[::1]" ||
      host.endsWith(".local")
    ) {
      return origin;
    }
    if (host.includes("staging")) return STAGING_LOGIN_LINK;
    // Deployed on a known admin host (either domain, while both are served during the
    // usedeex.com cutover) — send the magic link back to whichever origin we're on, so a
    // stale bundle on the old domain still produces a link that works.
    if (
      host.endsWith("admin.usedeex.com") ||
      host.endsWith("admin.deexoptions.com")
    ) {
      return origin;
    }
    return IS_STAGING ? STAGING_LOGIN_LINK : PROD_LOGIN_LINK;
  };

  const validateEmail = (email: string) => {
    // check if email is empty
    if (!email) {
      setError("Kindly tell us your mail");
      setTimeout(() => {
        setError("");
      }, 5000);
      return false;
    }
    // check if email is in the correct format using a regular expression
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(email)) {
      setError("Your email is not in the correct format");
      setTimeout(() => {
        setError("");
      }, 6000);
      return false;
    }

    setError("");
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    if (!loading) {
      setLoading(true);
      axios
        .post(`${API_URL}api/auth/request-signin-link`, {
          email,
          linkFormat: `${resolveMagicLinkBase()}/verify-mail?token=<TOKEN>`,
        })
        .then((r: any) => {
          setLoading(false);
          setEmail("");
          setSuccess("A login link has been sent to your mail, kindly check.");
          setTimeout(() => {
            setSuccess("");
          }, 3000);
        })
        .catch((e: any) => {
          setError(e.response.data.message);
          setLoading(false);
          setTimeout(() => {
            setError("");
          }, 3000);
          console.log(e.response.data.message);
        });
    }
  };

  // Already signed in (the session cookie survived a reload)? Skip the login screen.
  useEffect(() => {
    if (authReady && isAuthenticated) navigate(from);
  }, [authReady, isAuthenticated, from, navigate]);

  return (
    <div className="w-full lgss:w-[60%] flex justify-start overflow-auto pb-16 lgss:justify-start   items-center flex-col bg-[#131A1F] font-manrope">
      <div className="lgss:hidden mt-24 ">
        <img src={logo} className="" alt="" />
      </div>

      <div className="flex w-full md:w-9/12 lgss:w-11/12 mt-18 lgss:mt-12 xxl:mt-32 flex-col justify-center items-center lgss:items-start lgss:px-4 lg:px-[40px] xxl:px-[100px] xxxl:px-[200px]">
        <h2 className="text-white font-sora w-11/12 lgss:w-full text-center lgss:text-left text-[24px] lg:text-[32px] font-bold mt-16 lgss:pt-0">
          Welcome to DeeXoptions Admin
        </h2>
        <h4 className="font-manrope text-white w-11/12 lgss:w-full text-center lgss:text-left text-[15px] lgss:text-[16px] mt-2">
          Choose a secure way to sign in
        </h4>

        <div
          className={`w-11/12 p-3 font-manrope bg-[#0E1316] rounded-[12px]  md:w-9/12 lgss:w-full gap-4 grid grid-cols-1 mds:grid-cols-3 mt-20 lgss:mt-12
      ${selectedMethod && "hidden  mds:grid"}`} // hide on small screens when a method is selected
        >
          <button
            onClick={() => setSelectedMethod("magiclink")}
            className={`rounded-[12px] h-[58px] flex gap-2 text-[15px] justify-center items-center ${
              selectedMethod === "magiclink"
                ? "bg-[#111B21] text-gray-50 font-bold"
                : selectedMethod === ""
                ? "bg-[#111B21] text-gray-50 font-bold"
                : "bg-transparent text-gray-500"
            }`}
          >
            <FaMagic className="text-[16px]" />
            Magic Link
          </button>
          <button
            onClick={() => setSelectedMethod("email")}
            className={`rounded-[12px] h-[58px] flex gap-2 text-[15px] justify-center items-center ${
              selectedMethod === "email"
                ? "bg-[#111B21] text-gray-50 font-bold"
                : selectedMethod === ""
                ? "bg-[#111B21] text-gray-50 font-bold"
                : "bg-transparent text-gray-500"
            }`}
          >
            <IoMailUnreadOutline className="text-[16px]" />
            Email Login
          </button>
          <button
            onClick={() => setSelectedMethod("auth-app")}
            className={`rounded-[12px] h-[58px] flex gap-2 text-[15px] justify-center items-center ${
              selectedMethod === "auth-app"
                ? "bg-[#111B21] text-gray-50 font-bold"
                : selectedMethod === ""
                ? "bg-[#111B21] text-gray-50 font-bold"
                : "bg-transparent text-gray-500"
            }`}
          >
            <CgScan className="text-[16px]" />
            OTP
          </button>
        </div>

        {/* Forms */}
        <div className="w-11/12  lgss:mt-0 lgss:w-full md:w-9/12">
          {selectedMethod === "magiclink" && (
            <form
              onSubmit={handleSubmit}
              className="lgss:mt-12 mt-12 md:mt-0  w-full "
            >
              {success && (
                <div className="bg-green-600 w-full text-white  text-[16px] rounded-xl justify-start items-center gap-4 flex h-[60px] px-2 font-bold mt-4 mb-4">
                  <AiOutlineCheckCircle className="text-3xl text-white" />
                  {success}
                </div>
              )}
              <label className="text-white font-bold text-[15px]">Email</label>
              <input
                type="email"
                value={email}
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                name="email"
                className="h-[58px] w-full mt-4  px-[16px]  font-manrope border-2 rounded-lg border-[#A0A9AD] outline-none bg-transparent  text-[#A0A9AD] transition duration-200"
              />

              {error && (
                <div className="bg-red-600 text-white mb-4 text-[12px] rounded-xl justify-start items-center gap-2 flex h-[50px] px-2 font-bold mt-4">
                  <BiErrorCircle className="text-xl" />
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-[63px] flex justify-center items-center font-manrope font-medium rounded-xl text-white bg-secondary_button mt-8 lgss:mt-8"
              >
                {loading ? (
                  <TailSpin color="#FFFFFF" height={30} width={30} />
                ) : (
                  "Send a login link"
                )}
              </button>
            </form>
          )}
          {selectedMethod === "email" && (
            <LoginWithEmail
              setOtpScreen={setOtpScreen}
              setOtpPayload={setOtpPayload}
            />
          )}
          {selectedMethod === "auth-app" && (
            <LoginWithOtp
              setCodeScreen={setCodeScreen}
              setCodeMail={setCodeMail}
              email={codeMail}
            />
          )}
        </div>
        {/* Back button for small screens */}
        {selectedMethod && (
          <button
            onClick={() => setSelectedMethod("")}
            className="mds:hidden mt-8 text-secondary_button font-medium font-manrope tracking-wide  text-[15px] "
          >
            ← Sign in with other method
          </button>
        )}
      </div>

      {otpScreen && (
        <OtpScreen
          setOtpScreen={setOtpScreen}
          otpPayload={otpPayload}
          setOtpPayload={setOtpPayload}
        />
      )}
      {codeScreen && (
        <CodeScreen
          setCodeScreen={setCodeScreen}
          codeMail={codeMail}
          codeOtp={codeOtp}
          setCodeOtp={setCodeOtp}
        />
      )}
    </div>
  );
};

const LoginPage = () => (
  <div className="flex flex-row h-screen bg-black">
    <Register />
    <Login />
  </div>
);

export default LoginPage;
